import type { Editor } from "./canvas";
import type { TLParentId, TLShapeId } from "@tldraw/tlschema";
import { sheetAt } from "./sheet-operations";

// Sheets remain selectable by click, but a marquee always selects
// editable contents, including objects that extend outside the paper boundary.
export function installSheetSelection(editor: Editor) {
  return editor.sideEffects.registerBeforeChangeHandler(
    "instance_page_state",
    (_prev, next) => {
      if (
        !editor.isIn("select.brushing") &&
        !editor.isIn("select.scribble_brushing")
      )
        return next;
      return {
        ...next,
        selectedShapeIds: next.selectedShapeIds.filter(
          (id) => editor.getShape(id)?.type !== "sheet",
        ),
      };
    },
  );
}

export function selectContents(editor: Editor, parentId?: TLParentId) {
  const selected = editor.getSelectedShapes();
  if (!parentId) {
    const single = selected.length === 1 ? selected[0] : undefined;
    if (single?.type === "sheet") parentId = single.id;
    else if (
      selected.length &&
      selected.every((s) => s.parentId === selected[0].parentId)
    )
      parentId = selected[0].parentId;
    else if (!selected.length)
      parentId = sheetAt(editor, editor.getViewportPageBounds().center)?.id;
  }
  const ids: TLShapeId[] = [];
  const visit = (parent: TLParentId) => {
    for (const id of editor.getSortedChildIdsForParent(parent)) {
      const shape = editor.getShape(id);
      if (
        !shape ||
        editor.isShapeOrAncestorLocked(shape) ||
        editor.isShapeHidden(shape)
      )
        continue;
      if (shape.type === "sheet") visit(shape.id);
      else ids.push(id);
    }
  };
  visit(parentId ?? editor.getCurrentPageId());
  editor.setCurrentTool("select");
  editor.markHistoryStoppingPoint("Select contents");
  editor.setSelectedShapes(ids);
}
